import { ReactNode } from "react";

import DocumentationIconButton from "components/DocumentationIconButton";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";
import FiltersMobileTriggerButton from "components/Filters/MobileTriggerButton";
import MobileView from "components/Mobile/MobileView";
import DesktopView from "components/Mobile/DesktopView";

import { FILTERS_ORDER_SETTINGS_KEY } from "./constants";
import BlueprintsFeatureGateTooltip from "./FeatureGate/Tooltip";
import { BlueprintActions } from "./FeatureGate/types";
import BlueprintsUpSell from "./UpSell";

type BlueprintsPageLayoutProps = {
  children?: ReactNode;
  onCreate?: () => void;
  showUpSell?: boolean;
  hideActions?: boolean;
  titleId: string;
};

const BlueprintsPageLayout = ({
  children,
  onCreate,
  showUpSell,
  hideActions,
  titleId,
}: BlueprintsPageLayoutProps) => {
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Blueprints · ${accountName}`);

  return (
    <>
      <ViewHeader firstLevel>
        <ViewHeaderWrapper>
          <ViewHeaderTitle id={titleId}>Blueprints</ViewHeaderTitle>
          <MobileView>
            {!hideActions && onCreate && (
              <BlueprintsFeatureGateTooltip
                action={BlueprintActions.CreateBlueprint}
                on={({ isDisabled, ...props }) => (
                  <Button {...props} onPress={onCreate} variant="primary" disabled={isDisabled}>
                    Create blueprint
                  </Button>
                )}
              />
            )}
          </MobileView>
        </ViewHeaderWrapper>

        {!hideActions && !showUpSell && (
          <ViewHeaderWrapper direction="row">
            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.BLUEPRINTS}
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
            <FiltersMobileTriggerButton />
            <DesktopView>
              <DocumentationIconButton
                href={getDocsUrl("/concepts/blueprint")}
                tooltipText="Go to blueprints documentation"
              />
              {onCreate && (
                <BlueprintsFeatureGateTooltip
                  action={BlueprintActions.CreateBlueprint}
                  on={({ isDisabled, ...props }) => (
                    <Button {...props} onPress={onCreate} variant="primary" disabled={isDisabled}>
                      Create blueprint
                    </Button>
                  )}
                />
              )}
            </DesktopView>
          </ViewHeaderWrapper>
        )}
      </ViewHeader>

      {showUpSell ? <BlueprintsUpSell /> : children}
    </>
  );
};

export default BlueprintsPageLayout;
